import { Stack, Tabs, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { productBussiness } from "api/bussiness/product/product-bussiness";
import { invalidateProductCatalogCache } from "api/cached";
import { ProductPermission } from "commons/enum/permission";
import { useCheckPermission } from "commons/hooks/use-check-permission";
import { observer } from "mobx-react-lite";
import { useStores } from "mobx-stores/_root-store";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  EditButton,
  GoBackIconButton,
  RemoveButton,
} from "share-components/mui-wrap/button/action-button";
import { XTab } from "share-components/mui-wrap/tab/x-tab";
import { PageContentScrollableContainer } from "share-components/templates/page/page-content-container";
import { PageDetailContainer } from "share-components/templates/page/page-detail-container";
import { PageHeaderContainer } from "share-components/templates/page/page-header-container";
import { EditProduct } from "../edit-product/edit-product";
import { FileManager } from "../file-manager/file-manager";
import { UpdateApproval } from "../update-approval/update-approval";
import { UpdateDimensionWeight } from "../update-dimention-and-weight/update-dimension-weight";
import { UpdateManufactoring } from "../update-manufactoring/update-manufactoring";
import { UpdateMaterial } from "../update-material/update-material";
import { AnalysisBillPreview } from "./analysis-bill-preview";
import { ApprovalInformationTable } from "./approval-information";
import { DimensionWeightInformationTable } from "./dimision-weight-information";
import { ManufactoringInformationTable } from "./manufatoring-information";
import { MaterialInformationTable } from "./material-information";
import { ProductBasicTable } from "./product-basic";

type Tab =
  | "product"
  | "material"
  | "DimensionAndWeight"
  | "approve"
  | "manufacturing"
  | "file";
export const ProductDetail = observer(() => {
  const {
    productManagement: {
      getProductDetail,
      openEdit,
      openUpdateDimensionAndWeight,
      openUpdateMaterial,
      openUpdateApproval,
      openUpdateManufactoring,
      openPrintPreview,
    },
  } = useStores();
  const [tab, setTab] = useState<Tab>("product");
  const { allowAccess: allowAccessImage } = useCheckPermission([
    ProductPermission.ViewFile,
  ]);
  const { productId } = useParams();
  useEffect(() => {
    if (productId) {
      getProductDetail(productId);
    }
  }, [getProductDetail, productId]);
  return (
    <PageDetailContainer>
      <PageHeaderContainer direction={"row"} justifyContent={"space-between"}>
        <Header tabSelected={tab}></Header>
      </PageHeaderContainer>
      <Tabs
        value={tab}
        onChange={(_, value) => {
          setTab(value);
        }}
      >
        <XTab label="Mặt hàng" value={"product"} />
        <XTab label="Vật tư" value={"material"} />
        <XTab label="Thông Số Kỹ Thuật" value={"DimensionAndWeight"} />
        <XTab label="Ký duyệt" value={"approve"} />
        <XTab label="Sản xuất" value={"manufacturing"} />
        {allowAccessImage && <XTab label="Hình ảnh" value={"file"} />}
      </Tabs>
      <PageContentScrollableContainer>
        {tab === "product" && <ProductBasicTable></ProductBasicTable>}
        {tab === "material" && (
          <MaterialInformationTable></MaterialInformationTable>
        )}
        {tab === "DimensionAndWeight" && (
          <DimensionWeightInformationTable></DimensionWeightInformationTable>
        )}
        {tab === "approve" && (
          <ApprovalInformationTable></ApprovalInformationTable>
        )}
        {tab === "manufacturing" && (
          <ManufactoringInformationTable></ManufactoringInformationTable>
        )}
        {tab === "file" && <FileManager></FileManager>}
      </PageContentScrollableContainer>

      <>
        {openPrintPreview && <AnalysisBillPreview></AnalysisBillPreview>}
        {openEdit && <EditProduct></EditProduct>}
        {openUpdateMaterial && <UpdateMaterial></UpdateMaterial>}
        {openUpdateDimensionAndWeight && (
          <UpdateDimensionWeight></UpdateDimensionWeight>
        )}
        {openUpdateApproval && <UpdateApproval></UpdateApproval>}
        {openUpdateManufactoring && <UpdateManufactoring></UpdateManufactoring>}
      </>
    </PageDetailContainer>
  );
});
type HeaderProps = {
  tabSelected: Tab;
};
const Header = observer(({ tabSelected = "product" }: HeaderProps) => {
  const queryClient = useQueryClient();
  const {
    productManagement: { setProp: setProductProps, productDetail },
    deleteConfirmationStore: { setProp, setDeletionCallback },
  } = useStores();
  const navigate = useNavigate();
  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);
  const onDelete = useCallback(
    async (id: string) => {
      if (id) {
        await productBussiness.delete(id);
        await invalidateProductCatalogCache(queryClient);
        goBack();
      }
    },
    [goBack, queryClient],
  );
  const removeHandler = useCallback(() => {
    setProp("open", true);
    setDeletionCallback(() => {
      if (!productDetail?.id) return;
      onDelete(productDetail?.id);
    });
  }, [onDelete, productDetail?.id, setDeletionCallback, setProp]);

  const editHandler = useCallback(() => {
    setProductProps("openEdit", true);
  }, [setProductProps]);

  const onUpdateMaterialClickHandler = useCallback(() => {
    setProductProps("openUpdateMaterial", true);
  }, [setProductProps]);

  const onUpdateDimensionAndWeightClickHandler = useCallback(() => {
    setProductProps("openUpdateDimensionAndWeight", true);
  }, [setProductProps]);

  const onUpdateApprovalClickHandler = useCallback(() => {
    setProductProps("openUpdateApproval", true);
  }, [setProductProps]);

  const onUpdateManufactoringClickHandler = useCallback(() => {
    setProductProps("openUpdateManufactoring", true);
  }, [setProductProps]);

  // const openPrintPreviewHandler = useCallback(() => {
  //   setProductProps("openPrintPreview", true);
  // }, [setProductProps]);
  return (
    <>
      <Stack
        direction={"row"}
        justifyItems={"center"}
        spacing={1}
        alignItems={"center"}
      >
        <GoBackIconButton onClick={goBack}></GoBackIconButton>
        <Typography variant="subtitle2">
          {`Thông tin chi tiết: `}
          <span style={{ color: "blue" }}>{`${productDetail?.name}${
            productDetail?.customer?.dealingName
              ? `- ${productDetail?.customer?.dealingName}`
              : ""
          }`}</span>
        </Typography>
      </Stack>
      <Stack direction={"row"} justifyItems={"center"} spacing={1}>
        {tabSelected === "product" && (
          <>
            <RemoveButton
              accessPermission={[ProductPermission.Delete]}
              onClick={removeHandler}
            ></RemoveButton>
            {/* <PrintPreviewButton
              accessPermission={[ProductPermission.PrintTestNote]}
              onClick={openPrintPreviewHandler}
            >
              In phiếu kiểm nghiệm
            </PrintPreviewButton> */}
            <EditButton
              accessPermission={[ProductPermission.Update]}
              onClick={editHandler}
            ></EditButton>
          </>
        )}
        {tabSelected === "material" && (
          <EditButton
            accessPermission={[ProductPermission.Material]}
            onClick={onUpdateMaterialClickHandler}
          >
            Cập nhật vật tư
          </EditButton>
        )}
        {tabSelected === "DimensionAndWeight" && (
          <EditButton
            accessPermission={[ProductPermission.DimensionWeight]}
            onClick={onUpdateDimensionAndWeightClickHandler}
          >
            Cập nhật thông số kỹ thuật
          </EditButton>
        )}

        {tabSelected === "approve" && (
          <EditButton
            accessPermission={[ProductPermission.Approve]}
            onClick={onUpdateApprovalClickHandler}
          >
            Cập nhật ký duyệt
          </EditButton>
        )}

        {tabSelected === "manufacturing" && (
          <EditButton
            accessPermission={[ProductPermission.Process]}
            onClick={onUpdateManufactoringClickHandler}
          >
            Cập nhật sản xuất
          </EditButton>
        )}
      </Stack>
    </>
  );
});

